import { Autocomplete, FormControl, TextField } from '@mui/material'
import { visuallyHidden } from '@mui/utils'
import { C1Chip } from 'c1ui/components/C1Chip'
import React, { FC } from 'react'
import { Control, Controller, ControllerRenderProps, Message } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { C1FormLabel } from './C1FormLabel'

export type C1LabeledChipsInputProps = {
    control?: Control<any> // eslint-disable-line @typescript-eslint/no-explicit-any
    name: string
    error?: boolean
    helperText?: Message
    label?: React.ReactNode
    hideLabel?: boolean
    placeholder?: string
    onChange?: (value: string[]) => void
    onBlur?: () => void
    value?: string[]
    disabled?: boolean
    lockedValues?: string[]
}
// This is a component that is used to create a text field where you can enter input to create chips
export const C1LabeledChipsInput = ({
    control, name, error, helperText, label, hideLabel, placeholder, onChange, value, onBlur, disabled, lockedValues,
}: C1LabeledChipsInputProps): React.ReactElement => {
    const intl = useIntl()
    const defaultPlaceholder = intl.formatMessage({
        id: 'component_labeled_chips_input_placeholder',
        defaultMessage: 'Hit enter to add a value',
    })

    const ChipsInput = <ChipsInputAutocompleted
        name={name}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        placeholder={(value?.length ?? 0) > 0 ? '' : placeholder ?? defaultPlaceholder}
        helperText={helperText}
        error={error}
        disabled={disabled}
        lockedValues={lockedValues}
    />

    const RHKChipsInput = <Controller
        name={name}
        control={control}
        render={({ field }): React.ReactElement => (
            <ChipsInputAutocompleted
                field={field}
                name={name}
                value={field.value}
                onChange={(values): void => {
                    field.onChange(values)
                }}
                onBlur={field.onBlur}
                placeholder={placeholder}
                helperText={helperText}
                error={error}
                disabled={disabled}
                lockedValues={lockedValues}
            />
        )}
    />

    return <FormControl fullWidth error={error} disabled={disabled}>
        <C1FormLabel label={label} htmlFor={name} style={hideLabel ? visuallyHidden : undefined} />
        {control ? RHKChipsInput : ChipsInput}
    </FormControl>
}

type ChipsInputAutocompletedProps = Omit<C1LabeledChipsInputProps, 'control' | 'label'> & {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    field?: ControllerRenderProps<any>
}

const ChipsInputAutocompleted: FC<ChipsInputAutocompletedProps> = ({
    field,
    name,
    value,
    onChange,
    onBlur,
    placeholder,
    helperText,
    error = false,
    disabled,
    lockedValues,
}) => {
    const isValueLocked = (val: string): boolean => lockedValues?.includes(val) ?? false

    return (
        <Autocomplete
            {...field}
            size="small"
            multiple
            id={name}
            options={[]}
            value={value}
            freeSolo
            disabled={disabled}
            renderTags={(val, getTagProps): React.ReactNode => {
                return val.map((option, index) => {
                    const tagProps = getTagProps({ index })
                    const locked = isValueLocked(option)
                    return (
                        <C1Chip
                            color={locked ? 'info' : 'primary'}
                            label={option}
                            {...tagProps}
                            // If value is locked, remove the onDelete handler to prevent removal
                            onDelete={locked ? undefined : tagProps.onDelete}
                        />
                    )
                })
            }}
            onChange={(_, values): void => {
                const validValues = values
                    .map((v) => v.trim())
                    .filter((v) => v.length > 0)
                    .filter((v, index, self) => self.indexOf(v) === index) // Remove duplicates

                // Ensure locked values are always included
                lockedValues?.forEach((locked) => {
                    if (!validValues.includes(locked)) {
                        validValues.unshift(locked)
                    }
                })

                onChange?.(validValues)
            }}
            onBlur={onBlur}
            renderInput={(params): React.ReactNode => (
                <TextField
                    {...params}
                    placeholder={placeholder}
                    helperText={helperText}
                    error={!!error}
                    InputProps={{
                        ...params.InputProps,
                    }}
                />
            )}
        />
    )
}
